/* eslint-disable jira/filename-case */
import {
	UFOExperience,
	ExperienceTypes,
	ExperiencePerformanceTypes,
} from '@atlassian/ufo/ufo-experimental';
import { createJpdExperience } from './createJpdExperience.tsx';
import type {
	ExperienceErrorReason,
	ExperienceMetadataExtra,
	JPDExperience,
} from './ExperienceMetadataExtra.types.tsx';

export const pageSegmentLoad: JPDExperience = createJpdExperience(
	new UFOExperience('jpd.idea-view', {
		type: ExperienceTypes.Load,
		performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	}),
);

// separate metric for loading fullscreen Idea View on start
export const directPageSegmentLoad: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view-direct', {
			type: ExperienceTypes.Load,
			performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
		}),
	);

export const summaryUpdate: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.summary-update', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export const descriptionUpdate: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.description-update', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export type CrossSiteSourceInstanceType = 'CLOUD' | 'DC' | 'UNKNOWN';

export type JpdExperienceDeliveryTicketCreateMetadata = {
	metadata: {
		isRemoteLinked: boolean;
		renderingLocation?: 'idea-view' | 'tab-overview' | 'tab-delivery';
		sourceInstanceType?: CrossSiteSourceInstanceType;
	};
};

export const deliveryTicketCreate: JPDExperience<
	ExperienceErrorReason,
	JpdExperienceDeliveryTicketCreateMetadata
> = createJpdExperience<ExperienceErrorReason, JpdExperienceDeliveryTicketCreateMetadata>(
	new UFOExperience('jpd.idea-view.delivery-ticket-create', {
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	}),
);

export type JpdExperienceDeliveryTicketExpandMetadata = {
	metadata: {
		isRemoteLinked: boolean;
		sourceInstanceType?: CrossSiteSourceInstanceType;
	};
};

export const deliveryTicketExpand = (): JPDExperience<
	ExperienceErrorReason,
	JpdExperienceDeliveryTicketExpandMetadata
> =>
	createJpdExperience<ExperienceErrorReason, JpdExperienceDeliveryTicketExpandMetadata>(
		new UFOExperience('jpd.idea-view.delivery-ticket-expand', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export const commentCreate: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.comment-create', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export const insightCreate: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.insight-create', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export const insightUpdate: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.insight-update', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export const insightDelete: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.insight-delete', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export type JpdExperienceIssueLinkCreateMetadata = {
	metadata: {
		isRemoteLinked: boolean;
		renderingLocation?: 'idea-view' | 'tab-overview' | 'tab-delivery';
		sourceInstanceType?: CrossSiteSourceInstanceType;
	};
};

export const issueLinkCreate: JPDExperience<
	ExperienceErrorReason,
	JpdExperienceIssueLinkCreateMetadata
> = createJpdExperience<ExperienceErrorReason, JpdExperienceIssueLinkCreateMetadata>(
	new UFOExperience('jpd.idea-view.issue-link-create', {
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	}),
);

export type JpdExperienceIssueLinkDeleteMetadata = {
	metadata: {
		isRemoteLinked: boolean;
		renderingLocation?: 'idea-view' | 'tab-overview' | 'tab-delivery';
		sourceInstanceType?: CrossSiteSourceInstanceType;
	};
};

export const issueLinkDelete: JPDExperience<
	ExperienceErrorReason,
	JpdExperienceIssueLinkDeleteMetadata
> = createJpdExperience<ExperienceErrorReason, JpdExperienceIssueLinkDeleteMetadata>(
	new UFOExperience('jpd.idea-view.issue-link-delete', {
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	}),
);

export const commentsSegmentLoad: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.comments', {
			type: ExperienceTypes.Load,
			performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
		}),
	);

export const insightsSegmentLoad: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.insights', {
			type: ExperienceTypes.Load,
			performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
		}),
	);

export const fileAttach: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.attach-file', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);

export const connectionsSegmentLoad: JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =
	createJpdExperience(
		new UFOExperience('jpd.idea-view.connections-tab', {
			type: ExperienceTypes.Load,
			performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
		}),
	);

/**
 * Since field updates can be frequent, makeFieldUpdate is a factory
 * to avoid conccurency problems as JPDExperience objects are singletons.
 */
export const makeFieldUpdate = (): JPDExperience<ExperienceErrorReason, ExperienceMetadataExtra> =>
	createJpdExperience(
		new UFOExperience('jpd.idea-view.field-update', {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
		}),
	);
